/**
 * web/src/lib/pinnedAttachments.ts
 * Server-pinned email attachments a gate build declares in
 * `gateData.context.pinnedAttachments` (e.g. the sponsor welcome email's logo
 * files, FP Connect 2026-10). The backend fetches the bytes at approve time.
 * The preview shows each with a thumbnail (images) or a download link, and the
 * reviewer can take one off — its Drive id rides the approve edits as
 * `removedPinnedAttachments`.
 */
import type { PinnedAttachment } from '../components/gates/EmailPreview';

/** Types a browser `<img>` can render as a thumbnail. */
const PREVIEWABLE_MIME = new Set(['image/png', 'image/jpeg', 'image/svg+xml', 'image/gif', 'image/webp']);

/**
 * Parse `gateData.context.pinnedAttachments` into preview chips.
 *
 * @param gateData the step's raw gate data
 * @param workflowId the gate's workflow — with `stepIndex`, enables preview
 *        links through `/api/workflows/:id/steps/:index/pinned-attachments/:fileId`
 * @param stepIndex the gate's step index
 * @returns the pinned chips, or undefined when there are none
 * @sideEffects none
 * @idempotent yes
 */
export function gatePinnedAttachments(
  gateData: unknown,
  workflowId?: string,
  stepIndex?: number,
): PinnedAttachment[] | undefined {
  if (!gateData || typeof gateData !== 'object') return undefined;
  const context = (gateData as Record<string, unknown>).context;
  if (!context || typeof context !== 'object') return undefined;
  const raw = (context as Record<string, unknown>).pinnedAttachments;
  if (!Array.isArray(raw)) return undefined;
  const out: PinnedAttachment[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const { filename, note, fileId, mimeType } = item as {
      filename?: unknown;
      note?: unknown;
      fileId?: unknown;
      mimeType?: unknown;
    };
    if (typeof filename !== 'string' || filename === '') continue;
    const chip: PinnedAttachment = {
      filename,
      note: `${typeof note === 'string' && note ? note : 'attached by the server'} · added on send`,
    };
    if (typeof fileId === 'string' && fileId !== '') {
      chip.fileId = fileId;
      if (workflowId && stepIndex !== undefined) {
        const url = `/api/workflows/${encodeURIComponent(workflowId)}/steps/${stepIndex}/pinned-attachments/${encodeURIComponent(fileId)}`;
        if (typeof mimeType === 'string' && PREVIEWABLE_MIME.has(mimeType)) {
          chip.previewUrl = url;
          chip.openUrl = url;
        } else {
          chip.openUrl = `${url}?download=true`;
        }
      }
    }
    out.push(chip);
  }
  return out.length > 0 ? out : undefined;
}
